import { useLingui } from '@lingui/react/macro';
import Big from 'big.js';
import type { ComponentProps } from 'react';

import { fractionToPercent, percentToFraction } from '@/shared/format/percent';

import { cn } from './cn';
import { fieldControlClass } from './input';
import type { MaskModel } from './internal/caret-mask';
import { useMaskedInput } from './internal/use-masked-input';

export type PercentFieldProps = Omit<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'type'> & {
  /** Fração como string decimal (`'0.0185'` = 1,85%). `null` = vazio. */
  readonly value: string | null | undefined;
  readonly onChange?: (fraction: string | null) => void;
  /** Casas do percentual exibido. Padrão 4 (fração com 6, como o `PERCENTAGE_DECIMAL_SCALE` do legado). */
  readonly decimalScale?: number;
  readonly maxIntegerDigits?: number;
};

function decimalSeparator(locale: string): string {
  return new Intl.NumberFormat(locale).formatToParts(1.5).find((part) => part.type === 'decimal')?.value ?? ',';
}

// Significativo canônico usa `.`; aceita `,` ou `.` digitados e exibe o separador do locale.
function percentModel(locale: string, decimalScale: number, maxIntegerDigits: number): MaskModel {
  const separator = decimalSeparator(locale);
  return {
    significant: (text) => {
      let integer = '';
      let fraction = '';
      let dot = false;
      for (const character of text) {
        if (character === ',' || character === '.') {
          if (!dot && decimalScale > 0) dot = true;
          continue;
        }
        if (character < '0' || character > '9') continue;
        if (dot) {
          if (fraction.length < decimalScale) fraction += character;
        } else if (integer.length < maxIntegerDigits) {
          integer += character;
        }
      }
      integer = integer.replace(/^0+(?=\d)/, '');
      if (dot && integer === '') integer = '0';
      return `${integer}${dot ? '.' : ''}${fraction}`;
    },
    render: (significant) => significant.replace('.', separator),
    anchor: 'left',
  };
}

function sameFraction(left: string | null, right: string | null): boolean {
  if (left === null || right === null) return left === right;
  try {
    return new Big(left).eq(new Big(right));
  } catch {
    return left === right;
  }
}

/** Percentual: exibe `1,85`, grava `'0.0185'` (fração em string decimal, sem float). */
export function PercentField({
  value,
  onChange,
  onBlur,
  decimalScale = 4,
  maxIntegerDigits = 3,
  className,
  ...props
}: PercentFieldProps) {
  const { i18n } = useLingui();
  const model = percentModel(i18n.locale, decimalScale, maxIntegerDigits);
  const bindings = useMaskedInput<string | null>({
    model,
    value: value ?? null,
    onChange,
    onBlur,
    toValue: (significant) => {
      const number = significant.replace(/\.$/, '');
      return number === '' ? null : percentToFraction(number);
    },
    fromValue: (fraction) => (fraction === null ? '' : model.significant(fractionToPercent(fraction))),
    sameValue: sameFraction,
    settle: (significant) => significant.replace(/\.$/, ''),
  });
  return (
    <div className="relative" data-slot="percent-field">
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        className={cn(fieldControlClass, 'pr-8 text-right tabular-nums', className)}
        {...props}
        {...bindings}
      />
      <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
        %
      </span>
    </div>
  );
}
